import { put, call, select, takeLatest, takeEvery } from "redux-saga/effects";
import * as actions from "./actions";
import { Endpoints } from "services/api";
import errorHandler from "common/errorHandler";
import _ from "lodash";

const getTenant = (state) => state.userLogin.tenant;
const getUser = (state) => state.userLogin.user;

function rangeQuery(range) {
  if (!range || (!range.from && !range.to)) {
    return "";
  }
  const params = [];
  if (range.from) params.push(`from=${encodeURIComponent(range.from)}`);
  if (range.to) params.push(`to=${encodeURIComponent(range.to)}`);
  return params.length ? `?${params.join("&")}` : "";
}

export function* getUsageSummaryRequest(api, { payload }) {
  try {
    const response = yield call([api, api.get], `${Endpoints.aiGateway}/api/v1/usage/summary${rangeQuery(payload.range)}`);
    yield put(actions.getUsageSummarySuccess(response.data));
  } catch (error) {
    yield put(actions.getUsageSummaryFailure(errorHandler(error, "Get", "usage summary")));
  }
}

export function* getUsageDailyRequest(api, { payload }) {
  try {
    const response = yield call([api, api.get], `${Endpoints.aiGateway}/api/v1/usage/daily${rangeQuery(payload.range)}`);
    yield put(actions.getUsageDailySuccess(response.data));
  } catch (error) {
    yield put(actions.getUsageDailyFailure(errorHandler(error, "Get", "daily usage")));
  }
}

export function* getUsageByModelRequest(api, { payload }) {
  try {
    const response = yield call([api, api.get], `${Endpoints.aiGateway}/api/v1/usage/by-model${rangeQuery(payload.range)}`);
    yield put(actions.getUsageByModelSuccess(response.data));
  } catch (error) {
    yield put(actions.getUsageByModelFailure(errorHandler(error, "Get", "usage by model")));
  }
}

export function* getUsageByCapabilityRequest(api, { payload }) {
  try {
    const response = yield call([api, api.get], `${Endpoints.aiGateway}/api/v1/usage/by-capability${rangeQuery(payload.range)}`);
    yield put(actions.getUsageByCapabilitySuccess(response.data));
  } catch (error) {
    yield put(actions.getUsageByCapabilityFailure(errorHandler(error, "Get", "usage by capability")));
  }
}

export function* getUsageBudgetRequest(api) {
  try {
    const response = yield call([api, api.get], `${Endpoints.aiGateway}/api/v1/usage/budget`);
    yield put(actions.getUsageBudgetSuccess(response.data));
  } catch (error) {
    yield put(actions.getUsageBudgetFailure(errorHandler(error, "Get", "budget")));
  }
}

export function* setUsageBudgetRequest(api, { payload }) {
  try {
    const response = yield call([api, api.put], `${Endpoints.aiGateway}/api/v1/usage/budget`, payload.budget);
    yield put(actions.setUsageBudgetSuccess(response.data));
  } catch (error) {
    yield put(actions.setUsageBudgetFailure(errorHandler(error, "Save", "budget")));
  }
}

// --- permission (resource: aigateway) ---
export function* getUsagePermissionRequest(api) {
  const permission = { canAdd: false, canView: false, canEdit: false, canDelete: false };
  try {
    const tenant = yield select(getTenant);
    const user = yield select(getUser);
    const response = yield call(
      [api, api.get],
      `${Endpoints.permissions}/api/v1/users/${user.userId}/permissions/resources/aigateway`,
      { tenantid: tenant.tenantId }
    );
    if (response && response.data && response.data.Items && response.data.Items.length > 0) {
      let permissionActions = response.data.Items.map((item) => item.actions);
      permissionActions = _.union(...permissionActions);
      permission.canAdd = permissionActions.includes("add");
      permission.canView = permissionActions.includes("view");
      permission.canEdit = permissionActions.includes("edit");
      permission.canDelete = permissionActions.includes("delete");
    }
    yield put(actions.getUsagePermissionSuccess(permission));
  } catch (error) {
    yield put(actions.getUsagePermissionFailure(errorHandler(error, "Get", "ai usage permission")));
  }
}

export default function* ({ api }) {
  yield takeLatest(actions.GET_USAGE_SUMMARY_REQUEST, getUsageSummaryRequest, api);
  yield takeLatest(actions.GET_USAGE_DAILY_REQUEST, getUsageDailyRequest, api);
  yield takeLatest(actions.GET_USAGE_BY_MODEL_REQUEST, getUsageByModelRequest, api);
  yield takeLatest(actions.GET_USAGE_BY_CAPABILITY_REQUEST, getUsageByCapabilityRequest, api);
  yield takeLatest(actions.GET_USAGE_BUDGET_REQUEST, getUsageBudgetRequest, api);
  yield takeLatest(actions.SET_USAGE_BUDGET_REQUEST, setUsageBudgetRequest, api);
  yield takeEvery(actions.GET_USAGE_PERMISSION_REQUEST, getUsagePermissionRequest, api);
}
